import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { Dropdown, List } from 'tea-component';
// @ts-ignore
import GridLayout, { WidthProvider } from 'react-grid-layout';
import { shallowEqual, useSelector, useIntl } from "umi";
import { camelCase } from 'lodash-es';
import 'react-grid-layout/css/styles.css';
import mydashboardBg from '@/pages/datalab/assets/img/mydashboard-bg/mydashboard-bg-12.png';
import mydashboardBg1 from '@/pages/datalab/assets/img/mydashboard-bg/mydashboard-bg-12-1.png';
import mydashboardBg2 from '@/pages/datalab/assets/img/mydashboard-bg/mydashboard-bg-12-2.png';
import mydashboardBg3 from '@/pages/datalab/assets/img/mydashboard-bg/mydashboard-bg-12-3.png';
import mydashboardBg4 from '@/pages/datalab/assets/img/mydashboard-bg/mydashboard-bg-12-4.png';
import { isChartTragCardType } from '@dbl/core/chartEditor';
import { DATALAB_EDITOR, } from '@dbl/core/global';
import { ChartType, type TChartType } from '@dbl/core/chart';
import { LANGUAGE_EN } from "@home/_config/config";
import { handleFilterValuesStruct } from "@/pages/datalab/components/DashboardLayout/_utils";
import type { IFilterValueRequest } from '@dbl/services/dashboard';
import {
  AI_ANALYSIS_TYPE,
  ChangeTabLayoutsType,
  gridLayoutConfig,
  type IUserFilterConfig,
  type TChangeTabLayoutsHandler,
  type TItemCallback,
  type TLayoutAiAnalysisType,
  type TLayoutChartType,
  type TLayoutFilterType,
  type TLayoutTextType,
} from '@dbl/core/dashboard';
import {
  TOOLTIP_MIN_TOP_PX,
  TOP_FILTER_ROW_HEIGHT_PX,
  TOP_FILTER_SECTION_GAP_PX,
} from './_config';
import TableTragCard from './components/DragShell/TableTragCard';
import ChartTragCard from './components/DragShell/ChartTragCard';
import ChartSwitchTragCard from './components/DragShell/ChartSwitchTragCard';
import TextDrag, { isTransparentTextCard } from './components/DragShell/TextDrag';
import Abnormal from '@/pages/home/components/Abnormal';
import BigNumberTragCard from "@/pages/datalab/components/DashboardLayout/components/DragShell/BigNumberTragCard";
import SvgIcon from "@home/components/SvgIcon";
import NumberTrendTragCard from "@/pages/datalab/components/DashboardLayout/components/DragShell/NumberTrendTragCard";
import DimNumberTrendTragCard
  from "@/pages/datalab/components/DashboardLayout/components/DragShell/DimNumberTrendTragCard";
import TopFilters from "@/pages/datalab/components/DashboardLayout/components/TopFilters";
import ContactShow from "@/pages/datalab/components/DashboardLayout/components/ContactShow";
import AiAnalysisCardView from './components/AiAnalysis/AiAnalysisCardView';
import type { IAiPromptInfo } from './components/AiAnalysis/types';
import { useAiAnalysisController } from './components/AiAnalysis/hooks/useAiAnalysisController';
import { useAiAnalysisBridge } from './components/AiAnalysis/hooks/useAiAnalysisBridge';
import { useAiPromptApplyConfigs } from './components/AiAnalysis/hooks/useAiPromptApplyConfigs';
import { resolveAiCardPromptInfo } from './components/AiAnalysis/utils/applyConfigUtils';
import { ChartCalculateMode } from '@dbl/core/chartEditor';
import './index.scss';

const ResponsiveGridLayout = WidthProvider(GridLayout);

const { cols, rowHeight } = gridLayoutConfig;

export interface IDashboardLayoutProps {
  gameCode: string;
  dashboardId: string;
  tabLayouts?: Array<{
    tabId: string;
    tabName: string;
    tabNameCN?: string;
    layouts: Array<{
      i?: string;
      type?: string;
      chartType?: string;
      task_id?: string;
      name?: string;
      name_cn?: string;
    }>;
  }>;
  filterValues?: IFilterValueRequest[] | null; // 接口请求筛选器参数
  layouts: (TLayoutChartType | TLayoutTextType | TLayoutAiAnalysisType)[];
  filters: TLayoutFilterType[];   // 筛选器
  userFilter: IUserFilterConfig;
  enableCollapseFilter?: boolean;
  system?: 'datalab' | 'dashboard';
  className?: string;
  chartExplore: boolean;
  userId?: string;
  showContact: boolean; // 报表底部是否显示联系人
  contactInfo: { email: string; username: string; }; // 联系人信息

  loading: boolean; // 报表加载状态
  error: boolean; // 报表加载失败状态
  isNoData: boolean; //

  /** 报表编辑相关 */
  isEdit: boolean;
  changeTabLayoutsReduce: TChangeTabLayoutsHandler;
  chartsInfoLoading: boolean; // 图表卡片的加载状态
  changeLeftLayoutSelectItem?: (item: TLayoutFilterType | IUserFilterConfig | null, isOpen?: boolean) => void; // 报表编辑时改变左侧点击状态
  onDrag?: TItemCallback;
  onResize?: TItemCallback;
  onResizeStop?: TItemCallback;
  onDragStart?: TItemCallback;
  onDragStop?: TItemCallback;
  onDrop?: TItemCallback;
  onDropDragOver?: (e: React.DragEvent<HTMLDivElement>) => ({ w?: number; h?: number; } | false);
  handleOnSubmit?: (() => void) | null;
  containerWidth?: number;  // 外层容器宽度
  showGridLine?: boolean; // 是否显示辅助线
  handleFilterDrop?: () => void; // 拖拽筛选器到报表筛选器区域中
}

interface IPrivilegesSelectorState {
  privilegesConfig: {
    allPrivileges: string[];
  };
}

const DashboardLayout: React.FC<IDashboardLayoutProps> = (props) => {
  const {
    system = 'datalab',
    isEdit = false,
    className,
    layouts,
    filters,
    userFilter,
    enableCollapseFilter = false,
    loading,
    error,
    isNoData = true,
    containerWidth = 0,

    changeTabLayoutsReduce,
    chartsInfoLoading,

    gameCode,
    dashboardId,
    tabLayouts,
    showGridLine = false,

    changeLeftLayoutSelectItem,
    handleOnSubmit,

    chartExplore,
    userId,
    showContact,
    contactInfo,
    handleFilterDrop,
    ...restProps
  } = props;

  const { allPrivileges } = useSelector((state: IPrivilegesSelectorState) => ({
    allPrivileges: state.privilegesConfig.allPrivileges,
  }), shallowEqual);
  const hasChartPrivilege = allPrivileges?.includes(DATALAB_EDITOR.Chart);

  const [open, setOpen] = useState(false); // 无权限时
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  /**
   * 图表计算需要上传的筛选器数据, 对象数组, 对象结构符合上传接口结构
   *    初始化设置 filters?.length > 0 ? null : [] 原因如下:
   *      * 在图表卡片内部通过判断 filterValues 存在时, 再发起计算请求, 避免重复请求
   *      * 但页面上没有筛选器时, 会无法发起图表计算请求; 所以判断页面没有筛选器时, 赋值空数组, 让图表卡片可以发起计算请求.
   */
  const [filterValues, setFilterValues] = useState<IFilterValueRequest[] | null>(filters?.length > 0 ? null : (userFilter?.controls?.length ? null : []));
  const [userFilterConfig, setUserFilterConfig] = useState<IUserFilterConfig | null>(null);
  const { hasAiCard, aiQueries, aiScopeTabs, aiScopeTaskIds } = useAiAnalysisBridge({
    layouts,
    tabLayouts,
    filterValues,
    userFilterConfig,
  });
  // 记录筛选器是否为页面初始进入状态, 或者点击了reset后
  const isFilterInitRef = useRef(true);
  const initialAiCardIdsRef = useRef<Set<string>>(new Set<string>());
  const hasCapturedInitialAiCardsRef = useRef(false);

  useEffect(() => {
    // 初始卡片快照只捕获一次：用于决定“首次挂载时哪些 AI 卡片需要 autoRun”。
    // layouts 在编辑/拖拽过程中可能会产生新引用，但为了避免重复 filter/map 与语义漂移，
    // 这里通过 hasCapturedInitialAiCardsRef + loading 做了双重兜底。
    if (hasCapturedInitialAiCardsRef.current || loading) {
      return;
    }
    const initialAiCardIds = layouts
      .filter((layoutItem) => layoutItem?.type === AI_ANALYSIS_TYPE || layoutItem?.chartType === AI_ANALYSIS_TYPE)
      .map((layoutItem) => String(layoutItem?.i || ''))
      .filter(Boolean);
    initialAiCardIdsRef.current = new Set<string>(initialAiCardIds);
    hasCapturedInitialAiCardsRef.current = true;
  }, [layouts, loading]);
  /**
   * 顶部筛选区（通用 + 用户）占用高度估算，用于首行图表 tooltip 纵向夹紧（与 TOOLTIP_MIN_TOP_PX 叠加）。
   * TopFilters 为双列栅格，行高与报表 body 栅格线 56px 对齐。
   */
  const topFiltersStackHeightPx = useMemo(() => {
    const generalCount = filters?.length ?? 0;
    const userCount = userFilter?.controls?.length ?? 0;
    const generalH =
      generalCount > 0
        ? Math.ceil(generalCount / 2) * TOP_FILTER_ROW_HEIGHT_PX
        : 0;
    const userH =
      userCount > 0
        ? Math.ceil(userCount / 2) * TOP_FILTER_ROW_HEIGHT_PX
        : 0;
    return (
      generalH +
      userH +
      (generalH > 0 && userH > 0 ? TOP_FILTER_SECTION_GAP_PX : 0)
    );
  }, [filters?.length, userFilter?.controls?.length]);

  // 删除卡片
  const deleteCard = (cardInfo: TLayoutChartType | TLayoutTextType | TLayoutAiAnalysisType) => {
    changeTabLayoutsReduce?.(ChangeTabLayoutsType.DELETE_CARD, { cardInfo });
  };

  const handleAiPromptInfoChange = useCallback((cardId: string, promptInfo: IAiPromptInfo) => {
    changeTabLayoutsReduce?.(ChangeTabLayoutsType.UPDATE_AI_ANALYSIS_CARD, {
      cardId,
      promptInfo,
    });
  }, [changeTabLayoutsReduce]);

  const {
    modalNode,
    promptOptions: aiPromptOptions,
    attachedCardId: aiAttachedCardId,
    attachedSessionId: aiAttachedSessionId,
    attachedSession: aiAttachedSession,
    runAttachedAnalysisByCardId: runAiAttachedAnalysisByCardId,
    onOpenAiAnalysisModal,
    onRegisterRuntimeApi,
    stopCardStreamingSession,
    onCloseAiAnalysisModal,
    handleAiAnalysisCardDeleted,
    releaseAttachedSessionByCardId: releaseAiAttachedSessionByCardId,
  } = useAiAnalysisController({
    isEdit: Boolean(isEdit),
    gameCode,
    dashboardId,
    queries: aiQueries,
    scopeTabs: aiScopeTabs,
    enabled: hasAiCard,
    onConfirmPromptInfo: handleAiPromptInfoChange,
  });

  const { applyPromptInfoByCardId, isLoadingApplyConfigs } = useAiPromptApplyConfigs({
    gameCode,
    dashboardId,
    allTaskIds: aiScopeTaskIds,
    promptOptions: aiPromptOptions,
    // 编辑态以布局内 promptInfo 为准，不请求「下次自动应用」配置
    enabled: hasAiCard && !isEdit,
  });

  const handleAiAnalysisDelete = useCallback((cardInfo: TLayoutAiAnalysisType | TLayoutChartType) => {
    const deletedCardId = String((cardInfo as TLayoutAiAnalysisType)?.i || (cardInfo as TLayoutChartType)?.i || '');
    if (!deletedCardId) {
      return;
    }
    handleAiAnalysisCardDeleted(deletedCardId);
    deleteCard(cardInfo);
  }, [deleteCard, handleAiAnalysisCardDeleted]);
  return (
    <div className="dbl-dashboard-contact-wrap">
      <Abnormal
        isLoading={loading || chartsInfoLoading}
        isError={error as unknown as boolean}
        isNoData={Number(filters?.length) === 0 && Number(userFilter?.controls?.length) === 0 && Number(layouts?.length) === 0 && isNoData}
        component={(<div className={classNames('dbl-dashboard-layout', className)}>
          <div className={'dbl-dashboard-content'}>
            {
              (Number(filters?.length) > 0 || Number(userFilter?.controls?.length) > 0) ? (<TopFilters
                generalFilters={filters}
                userFilter={userFilter}
                enableCollapseFilter={enableCollapseFilter}
                system={system}
                handleFilterDrop={handleFilterDrop}
                onConfirm={(values, isFilterInit) => {
                  const { generalFilters, userFilter } = values;
                  isFilterInitRef.current = isFilterInit;
                  generalFilters && setFilterValues(handleFilterValuesStruct(generalFilters));
                  userFilter && setUserFilterConfig(userFilter);
                }}
                onReset={(values) => {
                  const { generalFilters, userFilter } = values;
                  isFilterInitRef.current = true;
                  setFilterValues(handleFilterValuesStruct(generalFilters));
                  userFilter && setUserFilterConfig(userFilter);
                }}
                gameCode={gameCode}
                isEdit={isEdit}
                changeTabLayoutsReduce={changeTabLayoutsReduce}
                changeLeftLayoutSelectItem={(changeLeftLayoutSelectItem ?? (() => { })) as any}

              />) : ''
            }
            <ResponsiveGridLayout
              style={{
                // width: containerWidth-20,
                backgroundPositionX: 20,
                backgroundSize: `${(containerWidth - 20) / 12}px 56px`, // 20 减 10左边 10是预留右边滚动条的间距
                backgroundImage:
                  isEdit && showGridLine
                    ? containerWidth <= 1050
                      ? `url(${mydashboardBg})`
                      : containerWidth <= 1200
                        ? `url(${mydashboardBg1})`
                        : containerWidth <= 1550
                          ? `url(${mydashboardBg2})`
                          : containerWidth <= 2000
                            ? `url(${mydashboardBg3})`
                            : `url(${mydashboardBg4})`
                    : null,
              }}
              layout={layouts}
              // 不知道为什么这两个属性设置false，预览页面还是可以拖动卡片
              isDraggable={isEdit}
              isResizable={isEdit} // 设置卡片能不能改变宽高
              className="dbl-dashboard-body-background"
              cols={cols}
              rowHeight={rowHeight}
              draggableHandle={isEdit ? ".dbl-drag-shell" : undefined}
              // draggableHandle={".dbl-drag-shell"}
              // 通过 draggableCancel 控制预览页面不能拖动卡片
              draggableCancel={isEdit ? undefined : '.dbl-drag-shell'}
              margin={[20, 20]}
              // containerPadding={[10, 20]}
              resizeHandles={['e', 's', 'se']}
              // verticalCompact={false}
              {...restProps}
            >
              {layouts?.map((item) => {
                // const onVisibleClose = (firstChartIndex === index) ? onForceClose : undefined;
                const chartType = item?.chartType || '';
                const mode = item?.mode || '';
                const transparentTextCard = isTransparentTextCard(item);
                const config = JSON.parse((item as any)?.column_infos || '{}');
                const rightConfig = config?.chartsCfgSave ?? {};
                const chartConfig = rightConfig[chartType]?.customizeSetting?.[camelCase(chartType)] ?? {};
                const { chart_switch: chartSwitch } = chartConfig ?? {};
                // 是否允许图表切换（表格、柱状图、折线图）
                const isAllowSwitchChartType = [ChartType.Table, ChartType.Bar, ChartType.Line]?.includes(chartType as ChartType);
                // 是否显示表格类型切换(明细模式没有图表切换)
                const isShowTableSwitch = chartType === ChartType.Table ? mode === ChartCalculateMode.Aggregate : true;
                // 是否显示图表切换工具栏
                const isShowChartSwitch = chartSwitch?.show && Array.isArray(chartSwitch?.chartTypes) && chartSwitch?.chartTypes?.length > 1;
                // 是否是图表切换卡片
                const isChartSwitchable = isAllowSwitchChartType && isShowTableSwitch && isShowChartSwitch;
                // 贴顶图表：基础 TOOLTIP_MIN_TOP_PX 再减去顶部通用/用户筛选区估算高度
                const tooltipMinTopPx = item?.y === 0 ? TOOLTIP_MIN_TOP_PX - topFiltersStackHeightPx : undefined;
                return <div
                  key={String(item?.i)}
                  className={classNames('dbl-drag-shell table-analysis', {
                    'dbl-drag-shell--transparent-text-card': transparentTextCard,
                  })}
                  style={{
                    borderRadius: '8px',
                    background: transparentTextCard ? 'transparent' : '#fff',
                    border: transparentTextCard ? 'none' : undefined,
                  }}
                  onClick={isEdit && changeLeftLayoutSelectItem ? () => (changeLeftLayoutSelectItem as any)(item) : undefined}
                >
                  {
                    item?.type === 'text' && <TextDrag
                      isEdit={isEdit}
                      data={item}
                      getDelete={deleteCard}
                    />
                  }
                  {
                    (chartType === AI_ANALYSIS_TYPE || item?.type === AI_ANALYSIS_TYPE) && (
                      <AiAnalysisCardView
                        isEdit={isEdit}
                        shouldAutoRunOnMount={!isEdit || initialAiCardIdsRef.current.has(String(item.i))}
                        onDelete={() => {
                          handleAiAnalysisDelete(item as TLayoutAiAnalysisType | TLayoutChartType);
                        }}
                        onRunAttachedAnalysis={async (params) => {
                          await runAiAttachedAnalysisByCardId(String(item.i), params);
                        }}
                        gameCode={gameCode}
                        dashboardId={dashboardId}
                        cardId={String(item.i)}
                        attachedCardId={aiAttachedCardId}
                        attachedSessionId={aiAttachedSessionId}
                        attachedSession={aiAttachedSession}
                        taskIds={aiScopeTaskIds}
                        queries={aiQueries}
                        promptOptions={aiPromptOptions}
                        promptInfo={resolveAiCardPromptInfo({
                          isEdit: Boolean(isEdit),
                          layoutPromptInfo: (item as TLayoutAiAnalysisType)?.promptInfo,
                          applyPromptInfo: applyPromptInfoByCardId[String(item.i)],
                          isApplyConfigLoading: isLoadingApplyConfigs,
                        })}
                        onPromptInfoChange={(promptInfo) => {
                          handleAiPromptInfoChange(item.i, promptInfo);
                        }}
                        onStopSession={(fallbackStop) => {
                          stopCardStreamingSession(String(item.i), fallbackStop);
                        }}
                        onReleaseAttachedSession={() => {
                          releaseAiAttachedSessionByCardId(String(item.i));
                        }}
                        onOpenAiAnalysisModal={onOpenAiAnalysisModal}
                        onRegisterRuntimeApi={onRegisterRuntimeApi}
                        onCloseAiAnalysisModal={onCloseAiAnalysisModal}
                      />
                    )
                  }

                  {
                    (item?.type !== 'text') && (!chartType) ? (<div className={'dbl-card-drag'}>

                      <div className="dbl-card-drag-flex">
                        <div className="dbl-card-drag-title">
                          <div className="dbl-card-drag-title__row">
                            <SvgIcon type="drag-icon" />
                            <span className="dbl-card-drag-title__text">
                              {isEn ? item?.name : (item?.name_cn || item?.name)}
                            </span>
                          </div>
                        </div>
                        <div className="dbl-card-icon-operation-box">
                          {
                            isEdit && <Dropdown
                              clickClose={false}
                              button={
                                <div className={`dbl-card-drag-operation ${open ? 'action' : ''}`}>
                                  <SvgIcon type="there-points" />
                                </div>
                              }
                              placementOffset={0}
                              open={open}
                              appearance="pure"
                              placement="bottom-end"
                              onOpenChange={(value) => {
                                setOpen(value);
                              }}
                            >
                              <List type="option">
                                <List.Item
                                  onClick={() => {
                                    setOpen(false);
                                    deleteCard(item);
                                  }}
                                >
                                  {formatMessage({ id: 'dbl_common_delete' })}
                                </List.Item>

                              </List>
                            </Dropdown>
                          }
                        </div>
                      </div>
                      <div style={{ margin: 'auto' }}>
                        {isEn ? 'No authority' : '无权限'}
                      </div>


                    </div>) : ''
                  }
                  {
                    // 图表类型切换卡片: Table/Bar/Line
                    isChartSwitchable && <ChartSwitchTragCard
                      tooltipMinTopPx={tooltipMinTopPx}
                      chartsInfoLoading={chartsInfoLoading}
                      dataMsg={item}
                      filterValues={filterValues}
                      userFilter={userFilterConfig}
                      deleteCard={deleteCard}
                      handleOnSubmit={handleOnSubmit}
                      hasChartPrivilege={hasChartPrivilege}
                      system={system}
                      isEdit={isEdit}
                      chartExplore={chartExplore}
                      chartType={chartType as TChartType}
                      chartConfig={chartConfig}
                      layoutGridColSpan={item?.w}
                    />
                  }
                  {
                    // chartList接口请求回来之前chartType可能是未更新之前的
                    (chartType === 'Table') && !isChartSwitchable && <TableTragCard
                      chartsInfoLoading={chartsInfoLoading}
                      dataMsg={item}
                      filterValues={filterValues}
                      userFilter={userFilterConfig}
                      deleteCard={deleteCard}
                      handleOnSubmit={handleOnSubmit}
                      hasChartPrivilege={hasChartPrivilege}
                      system={system}
                      isEdit={isEdit}
                      chartExplore={chartExplore}
                      layoutGridColSpan={item?.w}
                    // onForceClose={onVisibleClose}
                    />
                  }
                  {
                    isChartTragCardType(chartType) && !isChartSwitchable && <ChartTragCard
                      tooltipMinTopPx={tooltipMinTopPx}
                      chartsInfoLoading={chartsInfoLoading}
                      isEdit={isEdit}
                      dataMsg={item}
                      filterValues={filterValues}
                      userFilter={userFilterConfig}
                      deleteCard={deleteCard}
                      handleOnSubmit={handleOnSubmit}
                      hasChartPrivilege={hasChartPrivilege}
                      chartExplore={chartExplore}
                      system={system}
                      chartType={chartType}
                      layoutGridColSpan={item?.w}
                    // onForceClose={onVisibleClose}
                    />
                  }
                  {
                    (chartType === ChartType.BigNumber) && <BigNumberTragCard
                      chartsInfoLoading={chartsInfoLoading}
                      isEdit={isEdit}
                      dataMsg={item}
                      filterValues={filterValues}
                      userFilter={userFilterConfig}
                      deleteCard={deleteCard}
                      handleOnSubmit={handleOnSubmit}
                      hasChartPrivilege={hasChartPrivilege}
                      chartExplore={chartExplore}
                      system={system}
                      // onForceClose={onVisibleClose}
                      isFilterInit={isFilterInitRef.current}
                    />
                  }

                  {
                    (chartType === ChartType.Trend) && <NumberTrendTragCard
                      chartsInfoLoading={chartsInfoLoading}
                      isEdit={isEdit}
                      dataMsg={item}
                      filterValues={filterValues}
                      userFilter={userFilterConfig}
                      deleteCard={deleteCard}
                      handleOnSubmit={handleOnSubmit}
                      hasChartPrivilege={hasChartPrivilege}
                      chartExplore={chartExplore}
                      system={system}
                      // onForceClose={onVisibleClose}
                      isFilterInit={isFilterInitRef.current}
                    />
                  }
                  {
                    (chartType === ChartType.DimNumberTrend) && <DimNumberTrendTragCard
                      chartsInfoLoading={chartsInfoLoading}
                      isEdit={isEdit}
                      dataMsg={item}
                      filterValues={filterValues}
                      userFilter={userFilterConfig}
                      deleteCard={deleteCard}
                      handleOnSubmit={handleOnSubmit}
                      hasChartPrivilege={hasChartPrivilege}
                      chartExplore={chartExplore}
                      system={system}
                      // onForceClose={onVisibleClose}
                      isFilterInit={isFilterInitRef.current}
                    />
                  }
                </div>;
              })}
            </ResponsiveGridLayout>
            {modalNode}
          </div>
        </div>)}
      />
      {
        (!loading && showContact) ? (<ContactShow email={contactInfo.email} userName={contactInfo.username} />) : ''
      }</div>

  );
};

export default DashboardLayout;